// The driver's pickup stop (05 §5.2, 04 §4.2 `completePickup`): what is typed at the customer's
// door, step by step, and the command it becomes. The cargo part is the counter's New shipment
// form (`counter-form`): the same draft, the same step checks, the same `collectCargo` payload and
// the same live price, so a shipment taken at the door is exactly the shipment the counter would
// have made. Only the shipper (known from the pickup request) and the till (a driver has none; the
// cash goes to the driver's custody) differ. No screen code here, so it is easy to test.
import type { PayloadOf } from '@ecobox/schema';
import {
  checkBarcode,
  buildCollectPayload,
  initialDraft,
  stepProblems,
  type Draft,
  type FormContext,
  type StepId,
} from './counter-form/draft';
import { dialCodeFor } from './counter-form/phone';
import type { OpsPickupRequest, OpsStop, StopGeo } from './ops-finalmile';

/** The screens of a pickup, in order. */
export const PICKUP_STEPS = [
  'visit',
  'drop',
  'boxes',
  'consignee',
  'contents',
  'pay',
  'proof',
] as const;
export type PickupStepId = (typeof PICKUP_STEPS)[number];

export const PICKUP_STEP_LABELS: Record<PickupStepId, string> = {
  visit: 'Customer',
  drop: 'Empty boxes',
  boxes: 'Boxes',
  consignee: 'Consignee',
  contents: 'Contents',
  pay: 'Payment',
  proof: 'Photos and signature',
};

/** Empty boxes left with the customer: a size, how many, and the barcodes scanned (optional). */
export interface DropDraft {
  sizeId: string;
  qty: number | undefined;
  barcodes: string[];
}

export interface PickupForm {
  drops: DropDraft[];
  /** Cash taken for empty boxes sold at the door. */
  emptyBoxCashMinor: number | undefined;
  /** Cargo is taken. When false the stop is an empty-box drop only (`cargo: null`). */
  takeCargo: boolean;
  /** The counter's draft: consignee, service, boxes, contents, payments. The shipper comes from the request. */
  cargo: Draft;
  /** Photo files are kept by the screen; the form only needs to know how many. */
  photoCount: number;
  signed: boolean;
}

/** The phone's `FormContext` for a pickup: no till (the cash goes to my custody), consignee phones in local form. */
export function pickupContext(
  base: Omit<FormContext, 'hasOpenTill' | 'consigneeDial'> & {
    destinationCountry?: string | undefined;
  },
): FormContext {
  const { destinationCountry, ...rest } = base;
  const consigneeDial = dialCodeFor(destinationCountry ?? base.city?.country ?? 'PH');
  return { ...rest, consigneeDial, hasOpenTill: true };
}

/** Who and what a pickup is for: the request when this phone has it, else what the stop carries. */
export interface PickupSubject {
  requestId?: string;
  /** On file: the shipper is this customer. Without it the contact becomes a new customer. */
  customerId?: string;
  contact?: { name: string; phoneE164: string };
  address?: OpsStop['address'];
  geo?: OpsStop['geo'];
  window?: OpsPickupRequest['window'];
  notes: string;
  wants: NonNullable<OpsStop['wants']>;
}

const NO_WANTS: PickupSubject['wants'] = { emptyBoxes: [], cargoPickup: false, estimatedBoxes: 0 };

/**
 * The stop carries the contact, the address and the wants (so a pickup opens with no signal); the
 * pickup request, when this phone has it, adds the customer on file, the window and the notes.
 */
export function pickupSubject(
  request: OpsPickupRequest | undefined,
  stop: Pick<OpsStop, 'contact' | 'address' | 'geo' | 'wants'> | undefined,
): PickupSubject {
  const phone = request?.contact.phoneE164 ?? stop?.contact?.phones[0];
  const name = request?.contact.name ?? stop?.contact?.name;
  const address = request?.address ?? stop?.address;
  const geo = request?.geo ?? stop?.geo;
  return {
    ...(request ? { requestId: request.id } : {}),
    ...(request?.customerId ? { customerId: request.customerId } : {}),
    ...(name && phone ? { contact: { name, phoneE164: phone } } : {}),
    ...(address ? { address } : {}),
    ...(geo ? { geo } : {}),
    ...(request ? { window: request.window } : {}),
    notes: request?.notes ?? '',
    wants: request?.wants ?? stop?.wants ?? NO_WANTS,
  };
}

/** A new pickup form: the customer is the shipper, the wanted boxes are the drop. */
export function initialPickupForm(subject: PickupSubject | undefined): PickupForm {
  const draft = initialDraft();
  draft.service = 'sea';
  draft.prohibitedAck = false;
  const contact = subject?.contact;
  if (contact) {
    draft.shipper = subject.customerId
      ? {
          mode: 'existing',
          customer: { id: subject.customerId, name: contact.name, phoneE164: contact.phoneE164 },
          name: contact.name,
          phone: contact.phoneE164,
          email: '',
        }
      : { mode: 'new', customer: null, name: contact.name, phone: contact.phoneE164, email: '' };
  }
  return {
    drops: (subject?.wants.emptyBoxes ?? []).map((e) => ({
      sizeId: e.sizeId,
      qty: e.qty,
      barcodes: [],
    })),
    emptyBoxCashMinor: undefined,
    takeCargo: subject?.wants.cargoPickup ?? false,
    cargo: draft,
    photoCount: 0,
    signed: false,
  };
}

// ---- checks ---------------------------------------------------------------------------------

/** The counter says "already in this shipment"; on the doorstep the box is "already scanned". */
function atThisStop<T extends { ok: false; error: string }>(result: T): T {
  return {
    ...result,
    error: result.error.replace(
      /is already in this shipment\./,
      'is already scanned at this stop.',
    ),
  };
}

/** A barcode scanned into an empty-box drop: a real EcoBox barcode, not already in any drop or cargo box. */
export function checkDropBarcode(
  form: PickupForm,
  raw: string,
): { ok: true; barcode: string } | { ok: false; error: string } {
  const all = [...form.drops.flatMap((d) => d.barcodes)];
  const checked = checkBarcode(
    raw,
    all.map((barcode) => ({ barcode, sizeId: '', sold: false })),
  );
  if (!checked.ok) return atThisStop(checked);
  if (form.cargo.boxes.some((b) => b.barcode === checked.barcode)) {
    return { ok: false, error: `${checked.barcode} is already a cargo box in this pickup.` };
  }
  return checked;
}

/** A barcode scanned as a cargo box: not already in the drop either. */
export function checkCargoBarcode(
  form: PickupForm,
  raw: string,
): { ok: true; barcode: string } | { ok: false; error: string } {
  const checked = checkBarcode(raw, form.cargo.boxes);
  if (!checked.ok) return atThisStop(checked);
  if (form.drops.some((d) => d.barcodes.includes(checked.barcode))) {
    return { ok: false, error: `${checked.barcode} was left as an empty box at this stop.` };
  }
  return checked;
}

export function dropProblems(drops: readonly DropDraft[]): string[] {
  const out: string[] = [];
  const sizes = new Set<string>();
  drops.forEach((d, i) => {
    const n = i + 1;
    if (d.sizeId === '') out.push(`Empty boxes ${n}: choose a size.`);
    else if (sizes.has(d.sizeId)) out.push(`Size ${d.sizeId} is listed twice. Combine them.`);
    sizes.add(d.sizeId);
    if (!d.qty || d.qty < 1) out.push(`Empty boxes ${n}: how many?`);
    else if (d.qty > 50) out.push(`Empty boxes ${n}: at most 50 of one size at a stop.`);
    if (d.qty && d.barcodes.length > d.qty) {
      out.push(`Empty boxes ${n}: ${d.barcodes.length} barcodes scanned for ${d.qty} boxes.`);
    } else if (d.qty && d.barcodes.length > 0 && d.barcodes.length < d.qty) {
      out.push(
        `Empty boxes ${n}: scan all ${d.qty} barcodes, or none (${d.barcodes.length} scanned).`,
      );
    }
  });
  return out;
}

const CARGO_STEP: Partial<Record<PickupStepId, readonly StepId[]>> = {
  boxes: ['service', 'boxes'],
  consignee: ['consignee'],
  contents: ['contents'],
  pay: ['pay'],
};

/** What is missing on one screen of the pickup, in words a driver understands. Empty means done. */
export function pickupStepProblems(
  step: PickupStepId,
  form: PickupForm,
  ctx: FormContext,
): string[] {
  if (step === 'visit') return [];
  if (step === 'drop') {
    return [
      ...dropProblems(form.drops),
      ...(form.drops.some((d) => d.qty) || form.takeCargo
        ? []
        : ['Nothing to do at this stop yet. Add empty boxes, take cargo, or mark it failed.']),
    ];
  }
  if (step === 'proof') {
    return form.signed ? [] : ['The customer signs on the screen.'];
  }
  if (!form.takeCargo) return [];
  const inner = CARGO_STEP[step] ?? [];
  const problems = inner.flatMap((id) => stepProblems(id, form.cargo, ctx));
  // The shipper is the customer on the request; only a new customer's phone needs checking here.
  if (step === 'boxes') problems.push(...stepProblems('shipper', form.cargo, ctx));
  return problems;
}

/** Every problem on every screen: the review shows these before "Save pickup". */
export function pickupProblems(form: PickupForm, ctx: FormContext): string[] {
  return PICKUP_STEPS.flatMap((s) => pickupStepProblems(s, form, ctx));
}

/** The screens a pickup goes through: cargo screens only when cargo is taken. */
export function pickupStepsFor(form: Pick<PickupForm, 'takeCargo'>): PickupStepId[] {
  return PICKUP_STEPS.filter(
    (s) => form.takeCargo || s === 'visit' || s === 'drop' || s === 'proof',
  );
}

/** Cash taken by the driver in total: the cargo payments plus empty boxes sold at the door. */
export function pickupCashMinor(form: PickupForm): number {
  const cargo = form.takeCargo
    ? form.cargo.payments
        .filter((p) => p.method === 'cash')
        .reduce((n, p) => n + (p.amountMinor ?? 0), 0)
    : 0;
  return cargo + (form.emptyBoxCashMinor ?? 0);
}

/** One cash payment of `amountMinor`, or none (the shipment is left unpaid). */
export function withCashPayment(form: PickupForm, amountMinor: number | undefined): PickupForm {
  const amount = amountMinor ?? 0;
  return {
    ...form,
    cargo: {
      ...form.cargo,
      payments: amount > 0 ? [{ key: 1, method: 'cash', amountMinor: amount, reference: '' }] : [],
    },
  };
}

// ---- the command ---------------------------------------------------------------------------------

export interface PickupNumbers {
  trackingNo: string;
  /** Leased only when money is taken. */
  receiptNo?: string | undefined;
  /** `typed`: the driver typed the tracking number by hand (D-27). */
  trackingSource?: 'generated' | 'typed' | undefined;
  /** The numbering series a generated number came from (03 §3.5). */
  seriesId?: string | undefined;
}

/** True when this pickup needs a tracking number (cargo), and a receipt number (a cash payment). */
export function numbersNeeded(form: PickupForm): { tracking: boolean; receipt: boolean } {
  const tracking = form.takeCargo;
  const receipt = (tracking && form.cargo.payments.some((p) => (p.amountMinor ?? 0) > 0)) || false;
  return { tracking, receipt };
}

/**
 * The `completePickup` payload. The cargo is the counter's own `collectCargo` payload (built by
 * `buildCollectPayload`); the signature is named in both places so the shipment keeps it.
 */
export function buildCompletePickupPayload(args: {
  runId: string;
  stopId: string;
  form: PickupForm;
  ctx: FormContext;
  numbers?: PickupNumbers | undefined;
  /** The price the phone showed. The server re-prices; a difference opens an exception. */
  expectedGrossMinor?: number | undefined;
  photoPaths: readonly string[];
  signaturePath?: string | undefined;
  geo?: StopGeo | undefined;
}): PayloadOf<'completePickup'> {
  const { form, ctx } = args;
  let cargo: PayloadOf<'completePickup'>['cargo'] = null;
  if (form.takeCargo) {
    if (!args.numbers) throw new Error('A tracking number is needed to take cargo.');
    if (args.expectedGrossMinor === undefined) throw new Error('The price is not worked out yet.');
    const built = buildCollectPayload(
      { ...form.cargo, seasonTags: form.cargo.seasonTags },
      ctx,
      args.numbers,
      args.expectedGrossMinor,
    );
    cargo = args.signaturePath ? { ...built, signaturePath: args.signaturePath } : built;
  }
  return {
    runId: args.runId,
    stopId: args.stopId,
    emptyBoxesDropped: form.drops
      .filter((d) => (d.qty ?? 0) > 0)
      .map((d) => ({ sizeId: d.sizeId, qty: d.qty ?? 0, barcodes: [...d.barcodes] })),
    cargo,
    emptyBoxCashMinor: form.emptyBoxCashMinor ?? 0,
    photos: [...args.photoPaths].slice(0, 10),
    signaturePath: args.signaturePath ?? '',
    ...(args.geo ? { geo: args.geo } : {}),
  };
}
